import React from "react";
import styled from "styled-components";
import dayjs from "dayjs";
import "dayjs/locale/ja";

import ToolTip from "../tooltip/ToolTip";
import { toYen } from "../../utils/money";

dayjs.locale("ja");

/**
 * 支払明細書 1 件分のリスト行（グローバルコンポーネント）。
 *
 * マイページ「支払明細書」タブで本人の明細を表示する。右端に PDF
 * ダウンロードアイコン。見た目は旧 SPA モック（プレビュー枠＋金額＋
 * 状態ラベル）を踏襲する。
 *
 * 管理者ページ（/payment）は表形式（`Table`）に移行したため、
 * 旧 `variant="admin"`（申請者表示 + ステータス切替メニュー）は廃止した。
 */
export interface PaymentListItemProps {
  /** 申請日（ISO 文字列 / Date）。支払い待ちのとき表示。 */
  applicationDate?: string | Date | null;
  /** 支払日（ISO 文字列 / Date）。支払い済みのとき表示。 */
  paymentDate?: string | Date | null;
  /** 金額（円・税込）。数値でも文字列でも可。 */
  paymentAmount: number | string;
  /** 支払い済みか。 */
  isPaymentCompleted?: boolean;
  /** 取消済みか（運用による申請取消。「振込完了まで〜」の代わりに取消済みを表示）。 */
  isCancelled?: boolean;
  /** PDF の URL（null/未指定なら DL 不可）。 */
  pdfUrl?: string | null;
  /** ダウンロード押下時。 */
  onDownload?: () => void;
}

const Preview = styled.div`
  height: 72px;
  aspect-ratio: 1 / 1.414;
  flex-shrink: 0;

  background-color: var(--soremo-background);
  border: 1px solid var(--soremo-border);
`;

const DownloadIcon = styled.span.attrs({
  className: "material-symbols-rounded u-fontsize-24 u-text-light-gray",
})<{ $disabled?: boolean }>`
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
  &:hover {
    color: ${({ $disabled }) =>
      $disabled ? "var(--soremo-light-gray)" : "var(--soremo-blue)"};
  }
`;

const formatDate = (value?: string | Date | null): string =>
  value ? dayjs(value).format("YYYY年MM月DD日") : "";

const PaymentListItem: React.FC<PaymentListItemProps> = ({
  applicationDate,
  paymentDate,
  paymentAmount,
  isPaymentCompleted = false,
  isCancelled = false,
  pdfUrl,
  onDownload,
}) => {
  const formattedPaymentAmount = toYen(paymentAmount).toLocaleString("ja-JP");
  const dateLabel = isPaymentCompleted
    ? `支払日: ${formatDate(paymentDate)}`
    : `申請日: ${formatDate(applicationDate)}`;
  const hasPdf = Boolean(pdfUrl);

  return (
    <div className="u-row u-gap8 u-w100">
      <Preview />
      <div className="u-col u-gap4 u-w100 u-border-bottom u-ptb16">
        <div className="u-row-between u-w100">
          <div className="u-col u-gap4">
            <ul className="u-w100">
              <li className="bodytext-11 u-text-light-gray">{dateLabel}</li>
              {isCancelled ? (
                <li className="bodytext-11 u-text-light-gray">
                  取消済みの申請です
                </li>
              ) : (
                !isPaymentCompleted && (
                  <li className="bodytext-11 u-text-blue">
                    振込完了までお待ちください
                  </li>
                )
              )}
            </ul>
            <ul className="u-row u-items-end u-gap4">
              <li className="heading-24 u-line-height-100">
                {formattedPaymentAmount}
              </li>
              <li className="bodytext-11 u-line-height-100">円（税込）</li>
            </ul>
          </div>

          <ToolTip
            description={hasPdf ? "PDFをダウンロード" : "PDFは準備中です"}
            placement="left"
          >
            <DownloadIcon
              $disabled={!hasPdf}
              onClick={() => {
                if (hasPdf) onDownload?.();
              }}
            >
              download
            </DownloadIcon>
          </ToolTip>
        </div>
      </div>
    </div>
  );
};

export default PaymentListItem;
